import { defineComponent, onMounted, ref } from "vue";
import { isNullOrEmpty } from "../../../skyward.base.extensions";
import { ClassNamesCollection, classNamesToUniqueList, OptionalVueProp, VueProp } from "../../core/vueHelpers";
import { classNames } from "../classes";

export interface LabeledDataProps
{
    id?: string;
    class?: ClassNamesCollection;
    labelText: string;
    labelId?: string;
    labelClass?: ClassNamesCollection;
    dataText?: string;
    dataId?: string;
    dataClass?: ClassNamesCollection;
    inline?: boolean;
    additionalHtml?: string;
    isForSelector?: boolean;
    centered?: boolean;
    useTitleMargin?: boolean;
}

const LabeledData = defineComponent<LabeledDataProps>({
    name: "LabeledData",
    props:
    {
        id: OptionalVueProp(String),
        class: OptionalVueProp<ClassNamesCollection>([Object, Array, String]),
        labelText: VueProp(String),
        labelId: OptionalVueProp(String),
        labelClass: OptionalVueProp<ClassNamesCollection>([Object, Array, String]),
        dataText: OptionalVueProp(String),
        dataId: OptionalVueProp(String),
        dataClass: OptionalVueProp<ClassNamesCollection>([Object, Array, String]),
        inline: OptionalVueProp(Boolean),
        additionalHtml: OptionalVueProp(String),
        isForSelector: OptionalVueProp(Boolean),
        useTitleMargin: OptionalVueProp(Boolean)
    },
    setup(props, { slots })
    {
        const { labelText, labelId, labelClass, dataText, dataId, dataClass, additionalHtml, isForSelector, useTitleMargin, inline } = props;

        const ddElementRef = ref<HTMLElement | null>(null);

        onMounted(() =>
        {
            const ddElement = ddElementRef.value;
            if (!ddElement || isNullOrEmpty(additionalHtml))
            {
                return;
            }

            const parser = new DOMParser();
            const doc = parser.parseFromString(additionalHtml, "text/html");

            ddElement.innerHTML += doc.body.innerHTML;
        });

        const classes = new Set([classNames.labeledData, ...classNamesToUniqueList(props.class)]);
        const labelClasses = new Set([classNames.labeledDataLabel, classNames.decorativeLabel, classNames.visualLabel, ...classNamesToUniqueList(labelClass)]);
        const dataClasses = new Set([classNames.labeledDataData, classNames.visualLabel, ...classNamesToUniqueList(dataClass)]);

        if (inline)
        {
            classes.add(classNames.inline);
        }

        if (isForSelector)
        {
            labelClasses.add(classNames.selectorLabel);
            dataClasses.add(classNames.selectorData);
        }

        if (useTitleMargin)
        {
            labelClasses.add(classNames.titleMargin);
        }

        return () =>
        {
            const children = slots.default?.();
            const hasContent = !isNullOrEmpty(dataText) || !isNullOrEmpty(additionalHtml) || !!children?.length;

            return (
                <div id={props.id} class={[...classes]}>
                    <dt id={labelId} class={[...labelClasses]}>
                        {labelText}
                    </dt>
                    <dd
                        ref={ddElementRef}
                        id={dataId}
                        class={[...dataClasses]}
                    >
                        {
                            !hasContent &&
                            <>
                                <span aria-hidden="true">{"-"}</span>
                                <span class={classNames.srOnly}>{"Blank"}</span>
                            </>
                        }
                        {
                            hasContent &&
                            <>
                                {dataText}
                                {children}
                            </>
                        }
                    </dd>
                </div>
            );
        };
    }
});

export default LabeledData;
